<script setup lang="ts">
  import { computed } from 'vue'

  import { useAppRouting } from '@/composables/useAppRouting'
  import { Routes } from '@/const/Routes'
  import { IbexaColorPair } from '@/enums/IbexaColorPair'
  import type { Topic } from '@/types/Topic'

  import Typography from '@/components/common/Typography.vue'

  const { getRouteWithParams } = useAppRouting()
  const props = defineProps<{
    item: Topic
    index?: number
    colorPair?: IbexaColorPair
    isDisabled?: boolean
  }>()

  const numberedLabel = computed(() => {
    if (props.index === undefined) return
    const number = props.index + 1
    const numberWithPrefix = number < 10 ? `0${number}` : number
    return `${numberWithPrefix}`
  })
</script>

<template>
  <li>
    <component
      :is="isDisabled ? 'span' : 'router-link'"
      :to="getRouteWithParams(Routes.Topic, item)"
      :class="[
        'topic-card',
        colorPair,
        {'topic-card--disabled': isDisabled}
      ]"
      @keydown.enter.stop
    >
      <div class="mid-section">
        <Typography
          v-if="numberedLabel && !isDisabled"
          variant="number-large"
        >
          {{ numberedLabel }}
        </Typography>

        <Typography
          variant="h3"
          class="title"
        >
          {{ item.title }}
        </Typography>

        <div :class="['divider', !item?.subTitle && 'hidden']" />

        <Typography
          v-if="item?.subTitle"
          class="subtitle"
          component="span"
        >
          {{ item.subTitle }}
        </Typography>
      </div>
    </component>
  </li>
</template>

<style lang="scss" scoped>
  @include color-pairs;

  li {
    width: 100%;

    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }

  .topic-card {
    --card-padding: 24px;
    --text-color: var(--color-pair-color);
    --background-color: var(--color-pair-background-color);

    width: 100%;
    padding: var(--card-padding);
    display: flex;
    align-items: center;
    align-content: center;
    gap: 8px;
    background-color: var(--background-color);
    color: var(--text-color);
    text-decoration: none;

    &:not(:last-child) {
      border-bottom: none;
    }

    &:not(&--disabled) {
      &:focus-visible,
      &:hover {
        background-color: var(--color-pair-hover-background-color);
      }

      &:hover,
      &:active {
        transition: all .3s;
        cursor: pointer;
      }

      &:focus {
        outline: 2px solid var(--ks-focusring);
        outline-offset: 2px;
      }
    }

    @include vgs-media-mobile {
      --card-padding: 24px;
    }

    .mid-section {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;

      .title,
      .subtitle {
        margin: 0;
      }

      .subtitle {
        @include vgs-media-desktop {
          margin-top: 6px;
        }
      }

      @include vgs-media-mobile {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }

      .divider {
        width: .0625rem;
        height: 48px;
        flex-shrink: 0;
        background-color: var(--text-color);
        margin: 0 24px;

        @include vgs-media-mobile {
          display: none;
        }

      }

      .hidden {
        visibility: hidden;
      }
    }
  }
</style>
